import type {
  CapacityStrategyId,
  CapacityStrategyRead,
  CapacityStrategyOutcome,
  MetalPriceState,
  StrategyLeverGroup,
} from '@/versions/v1/schema/capacity';
import { STRATEGY_PRICE_LIMITS } from '@/versions/v1/shared/capacity-seed';
import { annualiseDailyA } from '@/versions/v1/shared/CapacityEngine';
import { formatCurrency } from './Scene.transform';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';

export type PriceControl = {
  key: keyof MetalPriceState;
  label: string;
  unit: string;
  min: number;
  max: number;
  step: number;
  value: number;
  display: string;
  minLabel: string;
  maxLabel: string;
  /** Track fill, 0–100. */
  fillPct: number;
  /** True once the price has left the deck price (A9's what-if). */
  moved: boolean;
  /** The Pb/Ag breakeven as a tick on the silver track, when it falls inside the range. */
  markPct?: number;
  markLabel?: string;
};

export type StrategyTabProjection = {
  priceDate: string;
  priceDateLabel: string;
  matrix: CapacityStrategyRead['matrix'];
  priceControls: readonly PriceControl[];
  circuitValues: {
    leadSilverA: number;
    leadSilverLabel: string;
    silverShareLabel: string;
    zincA: number;
    zincLabel: string;
    leadSilverBarPct: number;
  };
  /** `shortName` fits the recommendation pill in the matrix corner. */
  recommendation: { id: CapacityStrategyId; name: string; shortName: string };
  selected: CapacityStrategyOutcome;
  comparison: readonly {
    id: 'current' | CapacityStrategyId;
    label: string;
    ebitdaA: number;
    ebitdaLabel: string;
    best: boolean;
  }[];
  currentEbitdaLabel: string;
  selectedEbitdaLabel: string;
  deltaA: number;
  deltaLabel: string;
  annualisedDeltaA: number;
  annualisedDeltaLabel: string;
  leverGroups: readonly {
    id: StrategyLeverGroup;
    label: string;
    valueLabel: string;
    rows: CapacityStrategyOutcome['levers'];
  }[];
  tradeoff: null | {
    zincInLeadConcentratePct: number;
    leadConcentrateZnThresholdPct: number;
    thresholdUsePct: number;
    thresholdLabel: string;
    zincConcentrateGradePct: number;
    zincConcentrateGradeMinimumPct: number;
    zincGradeLabel: string;
    zincRecoveryPct: number;
    balancedZincRecoveryPct: number;
    zincRecoveryLabel: string;
    netDeltaA: number;
    netDeltaLabel: string;
  };
  breakevenAgOz: number | null;
  breakevenLabel: string;
  draftState: string | null;
  /** Per-ppt pair: the larger circuit is the reference; the tab appends its caption. */
  circuitCaptions: {
    leadSilverFigure: string;
    zincFigure: string;
    leadSilver: string;
    leadSilverIsReference: boolean;
    zinc: string;
    zincIsReference: boolean;
    leadSilverBarPct: number;
    zincBarPct: number;
  };
  matrixView: StrategyMatrixView;
  agLine: AgLineView | null;
  financial: {
    currentLabel: string;
    currentValue: string;
    selectedLabel: string;
    selectedValue: string;
    deltaValue: string;
    deltaTone: MoneyTone;
    annualised: string;
  };
};

export type MoneyTone = 'ok' | 'bad' | 'neutral';

export type StrategyMatrixBar = {
  /** Width of the grey Current bar, 0–100 on the header scale. */
  basePct: number;
  /** The strategy's move from Current: left edge and width, 0–100. */
  deltaLeftPct: number;
  deltaWidthPct: number;
  deltaTone: MoneyTone;
};

export type StrategyMatrixCellView = {
  /** '—' when the lever stays at its current setpoint with no priced effect. */
  value: string;
  unchanged: boolean;
  impact?: string;
  impactTone?: MoneyTone | 'warn';
  valueTone?: 'warn' | 'bad';
};

export type StrategyMatrixView = {
  scaleLabel: string;
  current: {
    name: string;
    ebitda: string;
    bar: StrategyMatrixBar;
    caption: string;
    feed: string;
    recoveries: string;
  };
  columns: readonly {
    id: CapacityStrategyId;
    name: string;
    best: boolean;
    draft: boolean;
    selected: boolean;
    ebitda: string;
    bar: StrategyMatrixBar;
    uplift: string;
    upliftTone: MoneyTone;
    annualised: string;
    feed: string;
    recoveries: string;
  }[];
  groups: readonly {
    id: StrategyLeverGroup | 'quality';
    label: string;
    rows: readonly {
      id: string;
      label: string;
      driver: string;
      current: string;
      cells: Record<CapacityStrategyId, StrategyMatrixCellView>;
    }[];
  }[];
  netVsBalanced: Record<CapacityStrategyId, StrategyMatrixCellView>;
  approval: string | null;
};

/** A label's glyph box in the mini chart's own coordinates (px). */
export type AgLabelBox = { left: number; right: number; top: number; bottom: number };

export type AgLineView = {
  width: number;
  height: number;
  zeroY: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  flip: {
    x: number;
    label: string;
    /** Text anchor point and baseline, so the UI draws the box the transform measured. */
    labelX: number;
    labelY: number;
    box: AgLabelBox;
    /** False when the label box leaves the 150 px plot: the tick still draws, the text does not. */
    textVisible: boolean;
  } | null;
  today: {
    x: number;
    y: number;
    /** The signed net, for the point's tooltip. */
    label: string;
  };
  /**
   * K11d: the net as one caption line under the plot — "today +US$5/day". The 150 px
   * chart is too narrow to hold the today label at every silver price, so the figure
   * lives outside it and never clips.
   */
  caption: string;
  title: string;
};

export type StrategyTabExtras = {
  /** The deck price, so a moved slider reads as a what-if. */
  deck?: MetalPriceState;
  /** The strategy read at the silver slider's two ends, for the Pb/Ag-minus-Balanced line. */
  agEnds?: { low: CapacityStrategyRead; high: CapacityStrategyRead };
  /** The feed instrument tag named in the feed-rate driver. */
  feedTag?: string;
};

const GROUPS: readonly { id: StrategyLeverGroup; label: string }[] = [
  { id: 'shared', label: 'Shared' },
  { id: 'lead-silver', label: 'Pb/Ag circuit' },
  { id: 'zinc', label: 'Zn circuit' },
];

export function effectiveStrategySelection(
  recommendedId: CapacityStrategyId,
  manualSelection: CapacityStrategyId | null,
): CapacityStrategyId {
  return manualSelection ?? recommendedId;
}

function signedA(value: number, suffix: string): string {
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${formatCurrency(Math.abs(value))}${suffix}`;
}

type PriceControlBase = Omit<PriceControl, 'fillPct' | 'moved' | 'markPct' | 'markLabel'>;

function pricePct(control: PriceControlBase, value: number): number {
  return Math.max(0, Math.min(100, ((value - control.min) / (control.max - control.min)) * 100));
}

export function strategyPriceControls(
  prices: MetalPriceState,
  deck?: MetalPriceState,
  breakevenAgOz: number | null = null,
): readonly PriceControl[] {
  const bases: readonly PriceControlBase[] = [
    {
      key: 'zincPerTonne', label: 'Zinc (Zn)', unit: `${CURRENCY_GLYPH}/dmt`,
      min: STRATEGY_PRICE_LIMITS.zincMin, max: STRATEGY_PRICE_LIMITS.zincMax,
      step: STRATEGY_PRICE_LIMITS.zincStep, value: prices.zincPerTonne,
      display: `${CURRENCY_GLYPH}${prices.zincPerTonne.toLocaleString('en-US')}`, minLabel: `${CURRENCY_GLYPH}1,500`, maxLabel: `${CURRENCY_GLYPH}4,000`,
    },
    {
      key: 'leadPerTonne', label: 'Lead (Pb)', unit: `${CURRENCY_GLYPH}/dmt`,
      min: STRATEGY_PRICE_LIMITS.leadMin, max: STRATEGY_PRICE_LIMITS.leadMax,
      step: STRATEGY_PRICE_LIMITS.leadStep, value: prices.leadPerTonne,
      display: `${CURRENCY_GLYPH}${prices.leadPerTonne.toLocaleString('en-US')}`, minLabel: `${CURRENCY_GLYPH}1,000`, maxLabel: `${CURRENCY_GLYPH}3,000`,
    },
    {
      key: 'silverPerOz', label: 'Silver (Ag)', unit: `${CURRENCY_GLYPH}/oz`,
      min: STRATEGY_PRICE_LIMITS.silverMin, max: STRATEGY_PRICE_LIMITS.silverMax,
      step: STRATEGY_PRICE_LIMITS.silverStep, value: prices.silverPerOz,
      display: `${CURRENCY_GLYPH}${prices.silverPerOz.toFixed(2)}`, minLabel: `${CURRENCY_GLYPH}15`, maxLabel: `${CURRENCY_GLYPH}${STRATEGY_PRICE_LIMITS.silverMax}`,
    },
  ];
  return bases.map((control) => {
    const breakevenInside = control.key === 'silverPerOz' && breakevenIsCrossover(breakevenAgOz);
    return {
      ...control,
      fillPct: pricePct(control, control.value),
      moved: deck !== undefined && Math.abs(deck[control.key] - control.value) > 1e-9,
      ...(breakevenInside && breakevenAgOz !== null ? {
        markPct: pricePct(control, breakevenAgOz),
        markLabel: `Pb/Ag breakeven ${CURRENCY_GLYPH}${breakevenAgOz.toFixed(2)}`,
      } : control.key === 'silverPerOz' ? {
        // No crossover on the slider: one strategy leads at every silver price, so no tick.
        markLabel: `${breakevenAgOz === null ? 'Balanced' : 'Pb/Ag max'} leads ${CURRENCY_GLYPH}${STRATEGY_PRICE_LIMITS.silverMin}–${STRATEGY_PRICE_LIMITS.silverMax}/oz`,
      } : {}),
    };
  });
}

/** A finite breakeven strictly inside the slider range is a crossover; one at the floor means Pb/Ag max leads at every price. */
function breakevenIsCrossover(breakevenAgOz: number | null): boolean {
  return breakevenAgOz !== null && breakevenAgOz > STRATEGY_PRICE_LIMITS.silverMin + 1e-9;
}

/**
 * The breakeven sentence. The engine returns the slider floor when Pb/Ag
 * maximum already leads there, and null when Balanced leads at the ceiling;
 * both read as a lead across the range, never as a breakeven.
 */
export function breakevenSentence(read: Pick<CapacityStrategyRead, 'breakevenAgOz' | 'prices'>): string {
  const min = STRATEGY_PRICE_LIMITS.silverMin;
  const max = STRATEGY_PRICE_LIMITS.silverMax;
  if (read.breakevenAgOz === null) {
    return `Balanced leads through ${CURRENCY_GLYPH}${max.toFixed(2)}/oz Ag at Zn ${CURRENCY_GLYPH}${read.prices.zincPerTonne.toLocaleString('en-US')}/dmt`;
  }
  if (!breakevenIsCrossover(read.breakevenAgOz)) {
    return `Pb/Ag max leads across ${CURRENCY_GLYPH}${min}–${max}/oz Ag`;
  }
  return `Pb/Ag maximum recovery breakeven ${CURRENCY_GLYPH}${read.breakevenAgOz.toFixed(2)}/oz · current ${CURRENCY_GLYPH}${read.prices.silverPerOz.toFixed(2)}/oz`;
}

export function strategyTabProjection(
  read: CapacityStrategyRead,
  selectedId: CapacityStrategyId,
  strategyCApproved: boolean,
  extras: StrategyTabExtras = {},
): StrategyTabProjection {
  const selected = read.strategies.find((row) => row.id === selectedId) ?? read.strategies[0]!;
  const recommendation = read.strategies.find((row) => row.id === read.recommendedId)!;
  const balanced = read.strategies.find((row) => row.id === 'balanced')!;
  const maxCircuitValue = Math.max(read.perPpt.leadSilverA, read.perPpt.zincA, 1);
  const comparison = [
    {
      id: 'current' as const,
      label: 'Current operating point',
      ebitdaA: read.currentEbitdaA,
      ebitdaLabel: `${formatCurrency(read.currentEbitdaA)}/day`,
      best: false,
    },
    ...read.strategies.map((row) => ({
      id: row.id,
      label: row.name,
      ebitdaA: row.financials.ebitdaA,
      ebitdaLabel: `${formatCurrency(row.financials.ebitdaA)}/day`,
      best: row.id === read.recommendedId,
    })),
  ];
  const deltaA = selected.financials.ebitdaA - read.currentEbitdaA;
  const annualisedDeltaA = annualiseDailyA(deltaA, read.daysPerYear);
  const groups = GROUPS.map((group) => ({
    ...group,
    valueLabel: group.id === 'shared'
      ? ''
      : `${formatCurrency(group.id === 'lead-silver' ? read.perPpt.leadSilverA : read.perPpt.zincA)}/pt`,
    rows: selected.levers.filter((row) => row.group === group.id),
  })).filter((group) => group.rows.length > 0);
  const tradeoff = selected.id === 'lead-silver-max'
    ? {
        zincInLeadConcentratePct: selected.quality.zincInLeadConcentratePct,
        leadConcentrateZnThresholdPct: selected.quality.leadConcentrateZnThresholdPct,
        thresholdUsePct: (selected.quality.zincInLeadConcentratePct /
          selected.quality.leadConcentrateZnThresholdPct) * 100,
        thresholdLabel: `${selected.quality.zincInLeadConcentratePct.toFixed(1)}% vs ${selected.quality.leadConcentrateZnThresholdPct.toFixed(1)}% Zn limit`,
        zincConcentrateGradePct: selected.quality.zincConcentrateGradePct,
        zincConcentrateGradeMinimumPct: selected.quality.zincConcentrateGradeMinimumPct,
        zincGradeLabel: `${selected.quality.zincConcentrateGradePct.toFixed(1)}% vs ${selected.quality.zincConcentrateGradeMinimumPct.toFixed(1)}% minimum`,
        zincRecoveryPct: selected.recoveries.znPct,
        balancedZincRecoveryPct: balanced.recoveries.znPct,
        zincRecoveryLabel: `${selected.recoveries.znPct.toFixed(1)}% vs ${balanced.recoveries.znPct.toFixed(1)}% Balanced`,
        netDeltaA: selected.financials.ebitdaA - balanced.financials.ebitdaA,
        netDeltaLabel: `${signedA(selected.financials.ebitdaA - balanced.financials.ebitdaA, '/day')} vs Balanced`,
      }
    : null;
  const breakevenLabel = breakevenSentence(read);
  const referenceA = Math.max(read.perPpt.leadSilverA, read.perPpt.zincA, 1);
  const zincIsReference = read.perPpt.zincA >= read.perPpt.leadSilverA;

  return {
    priceDate: read.priceDate,
    priceDateLabel: `deck ${new Date(`${read.priceDate}T00:00:00Z`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })}`,
    matrix: read.matrix,
    priceControls: strategyPriceControls(read.prices, extras.deck, read.breakevenAgOz),
    circuitValues: {
      leadSilverA: read.perPpt.leadSilverA,
      leadSilverLabel: `${formatCurrency(read.perPpt.leadSilverA)}/day`,
      silverShareLabel: `${read.perPpt.silverSharePct.toFixed(0)}% Ag share`,
      zincA: read.perPpt.zincA,
      zincLabel: `${formatCurrency(read.perPpt.zincA)}/day`,
      leadSilverBarPct: (read.perPpt.leadSilverA / maxCircuitValue) * 100,
    },
    recommendation: { id: recommendation.id, name: recommendation.name, shortName: STRATEGY_SHORT_NAME[recommendation.id] },
    selected,
    comparison,
    currentEbitdaLabel: `${formatCurrency(read.currentEbitdaA)}/day`,
    selectedEbitdaLabel: `${formatCurrency(selected.financials.ebitdaA)}/day`,
    deltaA,
    deltaLabel: `${signedA(deltaA, '/day')}`,
    annualisedDeltaA,
    annualisedDeltaLabel: `${signedA(annualisedDeltaA, '/yr')}`,
    leverGroups: groups,
    tradeoff,
    breakevenAgOz: read.breakevenAgOz,
    breakevenLabel,
    draftState: selected.id === 'lead-silver-max' && !strategyCApproved
      ? 'Draft — not live until approved'
      : null,
    circuitCaptions: {
      leadSilverFigure: formatCurrency(read.perPpt.leadSilverA),
      zincFigure: formatCurrency(read.perPpt.zincA),
      leadSilver: zincIsReference
        ? `${read.perPpt.silverSharePct.toFixed(0)}% Ag share · ${((read.perPpt.leadSilverA / referenceA) * 100).toFixed(0)}% of Zn`
        : `${read.perPpt.silverSharePct.toFixed(0)}% Ag share`,
      leadSilverIsReference: !zincIsReference,
      zinc: `${((read.perPpt.zincA / referenceA) * 100).toFixed(0)}% of Pb/Ag`,
      zincIsReference,
      leadSilverBarPct: (read.perPpt.leadSilverA / referenceA) * 100,
      zincBarPct: (read.perPpt.zincA / referenceA) * 100,
    },
    matrixView: strategyMatrixView(read, selected.id, strategyCApproved, extras.feedTag),
    agLine: extras.agEnds ? agLineView(read, extras.agEnds) : null,
    financial: {
      currentLabel: `Current EBITDA · ${CURRENCY_GLYPH}/day`,
      currentValue: formatCurrency(read.currentEbitdaA).replace(CURRENCY_GLYPH, ''),
      selectedLabel: `${selected.name} EBITDA · ${CURRENCY_GLYPH}/day`,
      selectedValue: formatCurrency(selected.financials.ebitdaA).replace(CURRENCY_GLYPH, ''),
      deltaValue: signedA(deltaA, ''),
      deltaTone: moneyTone(deltaA),
      annualised: `Annualised · ${signedA(annualisedDeltaA, '/yr')}`,
    },
  };
}

function moneyTone(value: number): MoneyTone {
  return value > 0.005 ? 'ok' : value < -0.005 ? 'bad' : 'neutral';
}

/** The header scale: round thousands around Current and every strategy, with room left of Current for its bar. */
function matrixScale(values: readonly number[]): { lo: number; hi: number } {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(max - min, 1_000);
  return {
    lo: Math.floor((min - span * 0.4) / 1_000) * 1_000,
    hi: Math.ceil((max + span * 0.15) / 1_000) * 1_000,
  };
}

function matrixBar(ebitdaA: number, currentA: number, scale: { lo: number; hi: number }): StrategyMatrixBar {
  const pct = (value: number) => Math.max(0, Math.min(100, ((value - scale.lo) / (scale.hi - scale.lo)) * 100));
  const base = pct(currentA);
  const end = pct(ebitdaA);
  return {
    basePct: base,
    deltaLeftPct: Math.min(base, end),
    deltaWidthPct: Math.abs(end - base),
    deltaTone: moneyTone(ebitdaA - currentA),
  };
}

function recoveriesLine(recoveries: { znPct: number; pbPct: number; agPct: number }): string {
  return `Zn ${recoveries.znPct.toFixed(1)}% · Pb ${recoveries.pbPct.toFixed(1)}% · Ag ${recoveries.agPct.toFixed(1)}%`;
}

const MATRIX_GROUPS: readonly { id: StrategyLeverGroup | 'quality'; label: string }[] = [
  { id: 'shared', label: 'Shared' },
  { id: 'lead-silver', label: 'Pb/Ag circuit' },
  { id: 'zinc', label: 'Zn circuit' },
  { id: 'quality', label: 'Concentrate quality' },
];

const STRATEGY_SHORT_NAME: Record<CapacityStrategyId, string> = {
  balanced: 'Balanced',
  'zinc-focus': 'Zn focus',
  'lead-silver-max': 'Pb/Ag max',
};

const STRATEGY_ORDER: readonly CapacityStrategyId[] = ['balanced', 'zinc-focus', 'lead-silver-max'];

/**
 * B's comparison matrix with A's strategy bars in its header: every strategy's
 * setpoints, priced contributions, concentrate quality and net against
 * Balanced in one read. Figures are the engine's matrix; this only formats them.
 */
export function strategyMatrixView(
  read: CapacityStrategyRead,
  selectedId: CapacityStrategyId,
  strategyCApproved: boolean,
  feedTag?: string,
): StrategyMatrixView {
  const matrix = read.matrix;
  const currentColumn = matrix.columns.find((column) => column.id === 'current')!;
  const strategyColumns = STRATEGY_ORDER.map((id) => matrix.columns.find((column) => column.id === id)!);
  const scale = matrixScale(matrix.columns.map((column) => column.ebitdaA));
  const outcomes = new Map(read.strategies.map((row) => [row.id, row]));
  const balanced = outcomes.get('balanced')!;
  const groups = MATRIX_GROUPS.map((group) => ({
    id: group.id,
    label: group.id === 'lead-silver'
      ? `${group.label} · ${formatCurrency(read.perPpt.leadSilverA)}/pt`
      : group.id === 'zinc'
        ? `${group.label} · ${formatCurrency(read.perPpt.zincA)}/pt`
        : group.label,
    rows: matrix.rows.filter((row) => row.group === group.id).map((row) => {
      const current = row.cells.current?.setpoint ?? '';
      const cells = Object.fromEntries(STRATEGY_ORDER.map((id) => {
        const cell = row.cells[id];
        if (!cell) return [id, { value: '—', unchanged: true }];
        if (row.group === 'quality') return [id, qualityCell(row.id, id, cell.setpoint, outcomes.get(id)!)];
        const unchanged = cell.setpoint === current && (cell.impactA === undefined || Math.abs(cell.impactA) < 0.005);
        if (unchanged) return [id, { value: '—', unchanged: true }];
        return [id, {
          value: cell.setpoint,
          unchanged: false,
          ...(cell.impactA !== undefined ? { impact: signedA(cell.impactA, '/day'), impactTone: moneyTone(cell.impactA) } : {}),
        }];
      })) as Record<CapacityStrategyId, StrategyMatrixCellView>;
      return {
        id: row.id,
        label: row.label,
        // The seed's feed-rate driver names the reference-basis target; the
        // matrix compares the measured feed with each strategy's own feed.
        driver: row.id === 'feed-rate' ? `Current ${feedTag ?? 'WIT-021'} feed against the strategy feed` : row.driver,
        current,
        cells,
      };
    }),
  })).filter((group) => group.rows.length > 0);
  const netVsBalanced = Object.fromEntries(STRATEGY_ORDER.map((id) => {
    const column = strategyColumns.find((row) => row.id === id)!;
    if (id === 'balanced') return [id, { value: '—', unchanged: true }];
    const outcome = outcomes.get(id)!;
    return [id, {
      value: signedA(column.netVsBalancedA, '/day'),
      unchanged: false,
      valueTone: column.netVsBalancedA < -0.005 ? 'bad' as const : undefined,
      ...(id === 'lead-silver-max'
        ? { impact: `Zn recovery ${outcome.recoveries.znPct.toFixed(1)}% vs ${balanced.recoveries.znPct.toFixed(1)}%`, impactTone: 'neutral' as const }
        : {}),
    }];
  })) as Record<CapacityStrategyId, StrategyMatrixCellView>;
  return {
    scaleLabel: `EBITDA · ${CURRENCY_GLYPH}${scale.lo / 1_000}k–${scale.hi / 1_000}k/day`,
    current: {
      name: currentColumn.name,
      ebitda: formatCurrency(currentColumn.ebitdaA),
      bar: matrixBar(currentColumn.ebitdaA, currentColumn.ebitdaA, scale),
      caption: 'per day today',
      feed: `${currentColumn.feedStph.toFixed(1)} stph`,
      recoveries: recoveriesLine(currentColumn.recoveries),
    },
    columns: strategyColumns.map((column) => {
      const id = column.id as CapacityStrategyId;
      return {
        id,
        name: column.name,
        best: id === read.recommendedId,
        draft: id === 'lead-silver-max' && !strategyCApproved,
        selected: id === selectedId,
        ebitda: formatCurrency(column.ebitdaA),
        bar: matrixBar(column.ebitdaA, currentColumn.ebitdaA, scale),
        uplift: signedA(column.upliftA, '/day'),
        upliftTone: moneyTone(column.upliftA),
        annualised: signedA(column.annualisedUpliftA, '/yr'),
        feed: `${column.feedStph.toFixed(1)} stph`,
        recoveries: recoveriesLine(column.recoveries),
      };
    }),
    groups,
    netVsBalanced,
    approval: strategyCApproved ? null : 'Pb/Ag maximum recovery needs approval (ZnSO₄ setpoint)',
  };
}

function qualityCell(
  rowId: string,
  strategyId: CapacityStrategyId,
  setpoint: string,
  outcome: CapacityStrategyOutcome,
): StrategyMatrixCellView {
  if (rowId === 'zinc-in-lead') {
    const share = outcome.quality.zincInLeadConcentratePct / outcome.quality.leadConcentrateZnThresholdPct;
    // Past 70% of the contractual Zn limit the cell states how close it is: the value stays ink and only the
    // share line reads as a warning (the comp); past the limit both read as a breach.
    if (share > 1) return { value: setpoint, unchanged: false, valueTone: 'bad', impact: `${(share * 100).toFixed(1)}% of limit`, impactTone: 'bad' };
    if (share >= 0.7) return { value: setpoint, unchanged: false, impact: `${(share * 100).toFixed(1)}% of limit`, impactTone: 'warn' };
    return { value: setpoint, unchanged: false };
  }
  if (rowId === 'zinc-concentrate-grade' && outcome.quality.zincConcentrateGradePct < outcome.quality.zincConcentrateGradeMinimumPct) {
    return { value: setpoint, unchanged: false, valueTone: 'bad' };
  }
  void strategyId;
  return { value: setpoint, unchanged: false };
}

/**
 * Approximate Inter advance width at the mini chart's 10 px labels, in px per
 * character. The longest label is ~5.67 px/char in the rendered figure, so 5.7 keeps
 * the box honest and slightly on the safe side.
 */
const AG_LABEL_CHAR = 5.7;
/** A 10 px label line's glyph box, measured against the rendered chart: cap top −10, descender +2. */
const AG_LABEL_ASCENT = 10;
const AG_LABEL_DESCENT = 2;

function agLabelWidth(label: string): number {
  return label.length * AG_LABEL_CHAR;
}

function agLabelBox(label: string, labelX: number, baseline: number, anchorEnd: boolean): AgLabelBox {
  const width = agLabelWidth(label);
  return {
    left: anchorEnd ? labelX - width : labelX,
    right: anchorEnd ? labelX : labelX + width,
    top: baseline - AG_LABEL_ASCENT,
    bottom: baseline + AG_LABEL_DESCENT,
  };
}

/**
 * C's Ag line: Pb/Ag maximum minus Balanced, US$/day, across the silver slider.
 * EBITDA is linear in the silver price, so the engine's two end reads fix the line;
 * today's point is the matrix's own net against Balanced.
 *
 * K11d: the mini chart is 150 px wide and the today label is wider than the free space
 * at some silver prices, so no in-plot placement can keep it from clipping. The net
 * reads as one caption under the chart instead. The flip keeps its tick and its
 * in-plot label; the label yields to the tick alone when its box leaves the chart.
 */
export function agLineView(
  read: CapacityStrategyRead,
  ends: { low: CapacityStrategyRead; high: CapacityStrategyRead },
): AgLineView {
  const net = (strategies: CapacityStrategyRead) =>
    strategies.strategies.find((row) => row.id === 'lead-silver-max')!.financials.ebitdaA -
    strategies.strategies.find((row) => row.id === 'balanced')!.financials.ebitdaA;
  const width = 150;
  const height = 66;
  const pad = { l: 4, r: 6, t: 20, b: 12 };
  const x0 = STRATEGY_PRICE_LIMITS.silverMin;
  const x1 = STRATEGY_PRICE_LIMITS.silverMax;
  const lowNet = net(ends.low);
  const highNet = net(ends.high);
  const todayNet = read.matrix.columns.find((column) => column.id === 'lead-silver-max')!.netVsBalancedA;
  const span = Math.max(Math.abs(lowNet), Math.abs(highNet), 1);
  const yMin = Math.min(lowNet, highNet, 0) - span * 0.1;
  const yMax = Math.max(lowNet, highNet, 0) + span * 0.1;
  const X = (value: number) => pad.l + (width - pad.l - pad.r) * (value - x0) / (x1 - x0);
  const Y = (value: number) => pad.t + (height - pad.t - pad.b) * (yMax - value) / (yMax - yMin);
  const todayY = Y(todayNet);
  const zeroY = Y(0);

  const flip = breakevenIsCrossover(read.breakevenAgOz) && read.breakevenAgOz !== null
    ? (() => {
        const x = X(read.breakevenAgOz);
        const label = `flips ${CURRENCY_GLYPH}${read.breakevenAgOz.toFixed(2)}`;
        const labelX = x - 5;
        const labelY = zeroY - 6;
        const box = agLabelBox(label, labelX, labelY, true);
        const textVisible = box.left >= 0 && box.right <= width && box.top >= 0 && box.bottom <= height;
        return { x, label, labelX, labelY, box, textVisible };
      })()
    : null;
  const todayLabel = signedA(todayNet, '/day');

  return {
    width,
    height,
    zeroY,
    x1: X(x0),
    y1: Y(lowNet),
    x2: X(x1),
    y2: Y(highNet),
    flip,
    today: { x: X(read.prices.silverPerOz), y: todayY, label: todayLabel },
    caption: `today ${todayLabel}`,
    title: `Pb/Ag maximum minus Balanced from ${signedA(lowNet, '/day')} at ${CURRENCY_GLYPH}${x0}/oz to ${signedA(highNet, '/day')} at ${CURRENCY_GLYPH}${x1}/oz Ag`,
  };
}
